<template>
  <DatatableReportV3
    v-if="tenantLoaded"
    ref="revenue_datatable"
    title="Revenue"
    :source="source"
    :config="config"
    :filters="filters"
  />
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { customerClassificationColumns } from "@/lib/customerTypes";

import CONSTANTS from "@/Constants.js";
import DepartmentLink from "@/Department/DepartmentLink.vue";
import JobOpportunityLink from "@/Job/Opportunity/JobOpportunityLink.vue";
import AccountingClassLink from "@/Reports/AccountingClassLink.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";
import RevenueActionsContextMenu from "./RevenueActionsContextMenu.vue";

export default {
  components: {
    DatatableReportV3
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters(["showAccountingClasses"]),
    ...mapState({
      tenantLoaded({ tenant }) {
        return !!tenant;
      }
    }),
    config() {
      return {
        responsive: true,
        order: [[0, "desc"]],
        columns: [
          {
            data: "completed_on",
            title: "Completed",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.days,
            filterName: "finalized_at",
            searchable: false,
            responsivePriority: 3
          },
          {
            data: "invoice",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.invoices,
            responsivePriority: 2
          },
          ...customerClassificationColumns(),
          {
            data: "customer",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers,
            responsivePriority: 1
          },
          {
            data: "membership_status",
            title: "Member Status",
            filterName: "membership_status",
            searchable: false,
            responsivePriority: 8
          },
          { data: "customer_email", title: "Email", responsivePriority: 12 },
          { data: "customer_phone", title: "Phone", responsivePriority: 6 },
          {
            data: "billing_address",
            title: "Billing Address",
            orderable: false,
            responsivePriority: 14
          },
          { data: "job", responsivePriority: 7 },
          {
            data: "tags",
            title: "Tags",
            orderable: false,
            searchable: false,
            responsivePriority: 16
          },
          {
            data: "location_name",
            title: "Location Name",
            orderable: false,
            responsivePriority: 15
          },
          {
            data: "service_address",
            title: "Service Address",
            orderable: false,
            responsivePriority: 13
          },
          {
            data: "department",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.departments,
            filterName: "department_id",
            searchable: false,
            responsivePriority: 9
          },
          {
            data: "technician",
            title: "Owner",
            filterName: "technician_id",
            searchable: false,
            responsivePriority: 10
          },
          {
            data: "accounting_class",
            title: "Class",
            orderable: false,
            searchable: false,
            visible: this.showAccountingClasses,
            responsivePriority: 18
          },
          {
            data: "invoice_last_emailed_at",
            title: "Last Emailed",
            searchable: false,
            responsivePriority: 17
          },
          {
            data: "batch_date",
            title: "Batched",
            searchable: false,
            responsivePriority: 11
          },
          {
            data: "revenue",
            className: "text-right",
            footerClassName: "aggregate-currency",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.revenue,
            searchable: false,
            responsivePriority: 5
          },
          {
            data: "balance",
            title: "Balance",
            className: "text-right",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.balance,
            footerClassName: "aggregate-currency",
            footerCellClass: "text-danger",
            filterName: "paid",
            searchable: false,
            responsivePriority: 4
          },
          {
            data: "actions",
            title: "",
            orderable: false,
            className: "text-center th-no-text",
            searchable: false
          }
        ],
        vueComponents: [
          {
            rootId: "department-modal-vue",
            component: DepartmentLink,
            options: {
              store
            }
          },
          {
            rootId: "job-opportunity-modal-vue",
            component: JobOpportunityLink,
            options: {
              store
            }
          },
          {
            rootId: "accounting-class-link-vue",
            component: AccountingClassLink,
            options: {
              store
            }
          },
          {
            rootId: "invoices-context-menu-vue",
            component: RevenueActionsContextMenu,
            options: {
              store
            }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          }
        ]
      };
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(td) {
  vertical-align: top !important;

  .delegated-icon {
    display: none;
  }
}
</style>
